import { PRODUCT_CARD_STYLE_OPTIONS } from "../theme";
/**
 * Per-section product-card style OVERRIDE. Sections that render product cards
 * (all-products, category-sections, category-tabs) include this so a seller can
 * give ONE section a different card design from the store-wide default.
 *
 * The card *drawing* is otherwise a theme-level setting (`productCardStyle`);
 * this optional field lets a single section opt out of it. The `inherit` value
 * (the default) means "use the store-wide style" — the buyer resolves
 * `section.productCardStyle ?? theme.productCardStyle`, so an unset section is
 * byte-identical to today. The real card options are spread from
 * `PRODUCT_CARD_STYLE_OPTIONS` (theme.ts), the single source of truth, so this
 * override never drifts from the global picker.
 *
 * A plain `select` (not `ui: "thumbnail"`): a per-section override reads best as
 * a labelled dropdown whose default line literally says "Same as store default",
 * and it needs no glyph in the two `heroVariantThumbs` copies.
 */
export function productCardStyleField() {
    return {
        id: "productCardStyle",
        type: "select",
        label: { ar: "شكل كروت المنتجات", en: "Product card style" },
        helpText: {
            ar: 'شكل كروت المنتجات في القسم ده. سيبه "زي المتجر" عشان ياخد الشكل العام.',
            en: "Card design for products in THIS section. Leave on “Same as store default” to follow the store-wide style.",
        },
        options: [
            {
                value: "inherit",
                label: { ar: "زي المتجر", en: "Same as store default" },
            },
            ...PRODUCT_CARD_STYLE_OPTIONS,
        ],
        default: "inherit",
    };
}
/**
 * Layout-variant picker for product-grid sections (all-products,
 * category-sections). Distinct values from other variant sets so the shared
 * thumbnail registry never collides.
 *
 * Layouts only arrange the grid — the card *drawing* is a theme-level
 * setting (`productCardStyle`), so it stays consistent across every section.
 */
export function productLayoutField() {
    return {
        id: "variant",
        type: "select",
        ui: "thumbnail",
        label: { ar: "نوع العرض", en: "Layout" },
        helpText: {
            ar: "طريقة عرض المنتجات.",
            en: "How the products are laid out.",
        },
        options: [
            { value: "standard", label: { ar: "شبكة", en: "Grid" } },
            { value: "slider", label: { ar: "شرائح", en: "Carousel" } },
            { value: "list", label: { ar: "قائمة", en: "List" } },
            { value: "masonry", label: { ar: "متداخل", en: "Masonry" } },
            { value: "featured", label: { ar: "مميز", en: "Featured" } },
        ],
        default: "standard",
    };
}
/**
 * Optional hairline divider between a section and its neighbours. Sections
 * that draw a 1px `var(--hairline)` border at their boundary (header,
 * value-strip, all-products, top-seller) include this so the seller can turn
 * the line off. Defaults to on to keep existing stores pixel-identical.
 */
export function dividerField(showIf) {
    return {
        id: "showDivider",
        type: "checkbox",
        label: { ar: "خط فاصل بين الأقسام", en: "Section divider line" },
        helpText: {
            ar: "خط رفيع يفصل القسم ده عن اللي جنبه.",
            en: "Thin hairline separating this section from its neighbours.",
        },
        default: true,
        ...(showIf ? { showIf } : {}),
    };
}
/**
 * Collection-grid layout picker — the product layouts plus a compact "strip"
 * variant (small standalone images with the name below, many in a row).
 */
export function collectionLayoutField() {
    return {
        id: "variant",
        type: "select",
        ui: "thumbnail",
        label: { ar: "نوع العرض", en: "Layout" },
        helpText: {
            ar: "طريقة عرض الفئات.",
            en: "How the collections are laid out.",
        },
        options: [
            { value: "standard", label: { ar: "شبكة", en: "Grid" } },
            // Same grid, but each tile's label is a rounded pill at the TOP of the
            // image instead of the bottom gradient block.
            {
                value: "overlay-pill",
                label: { ar: "شبكة بكبسولة", en: "Grid + pill label" },
            },
            { value: "slider", label: { ar: "شرائح", en: "Carousel" } },
            { value: "list", label: { ar: "قائمة", en: "List" } },
            { value: "masonry", label: { ar: "متداخل", en: "Masonry" } },
            { value: "featured", label: { ar: "مميز", en: "Featured" } },
            { value: "mini", label: { ar: "شريط مصغّر", en: "Compact strip" } },
            // Mobile-native "category chips" rows (horizontal snap-scroll). Designed
            // for phone widths; usable on any breakpoint via per-device overrides.
            { value: "chips-circle", label: { ar: "دوائر", en: "Circle chips" } },
            { value: "chips-tiles", label: { ar: "مربعات", en: "Tile chips" } },
            { value: "chips-pills", label: { ar: "كبسولات", en: "Pill chips" } },
            // Ports of the EasyOrders category rows. Each is a distinct engine of
            // theirs, not a restyle of one of ours — see
            // docs/plans/2026-07-19-storefront-section-vocabulary.md.
            { value: "bento", label: { ar: "بينتو", en: "Editorial bento" } },
            { value: "rail", label: { ar: "شريط جانبي", en: "Heading rail" } },
            { value: "scrim-tile", label: { ar: "مربعات معتمة", en: "Scrim tiles" } },
            {
                value: "caption-tile",
                label: { ar: "مربعات بعناوين", en: "Caption tiles" },
            },
            { value: "portrait", label: { ar: "بورتريه", en: "Portrait" } },
            {
                value: "pill-portrait",
                label: { ar: "بورتريه بشارة", en: "Pill portrait" },
            },
            {
                value: "avatar-chips",
                label: { ar: "دوائر الفئات", en: "Avatar chips" },
            },
            // Ported for the `nature` family (2026-07-20). Both are CATEGORY rows —
            // their content model is the store's collections, which is why they are
            // layouts here rather than new section types.
            {
                value: "ticker",
                label: { ar: "شريط متحرك", en: "Marquee ticker" },
            },
            {
                value: "hot-cards",
                label: { ar: "بطاقات بارزة", en: "Promo cards" },
            },
            // Ported for the `blast` family (2026-07-20). Their `ab-cat-card` here
            // is a HORIZONTAL card — a 64px circle with the name set BESIDE it —
            // where `avatar-chips` (prime) is an 84px circle with the name BENEATH.
            // Kept separate for the same reason `avatar-chips` was kept separate
            // from `chips-circle`: the axis is the layout, and folding them would
            // need a second control to unfold them again.
            {
                value: "avatar-row",
                label: { ar: "دوائر بأسماء جنبها", en: "Avatar rows" },
            },
        ],
        default: "standard",
    };
}
/**
 * Optional "eyebrow" label — a small mono uppercase line above a section's
 * title (e.g. "OUR STORY", "WHAT'S NEW"). Empty by default, so it renders
 * nothing until the seller fills it. Shared across sections so every section
 * offers the same eyebrow control. Sections that already declare their own
 * `eyebrow` (hero, stories, top-seller, about) keep theirs.
 */
export function eyebrowField(showIf) {
    return [
        {
            id: "eyebrow",
            type: "text",
            i18n: true,
            label: {
                ar: "نص صغير فوق العنوان (اختياري)",
                en: "Eyebrow label (optional)",
            },
            helpText: {
                ar: "نص صغير فوق العنوان.",
                en: "A small label shown above the title.",
            },
            validate: { max: 40 },
            ...(showIf ? { showIf } : {}),
        },
    ];
}
/**
 * Field ids that belong in the inspector's "Style" group. The Inspector
 * partitions a section's settings by membership in this set so all the
 * appearance controls collapse under one header. Includes both the
 * universal color fields and the optional button-related fields — a
 * section either has the button fields in its schema or it doesn't.
 */
export const APPEARANCE_FIELD_IDS = new Set([
    "backgroundColor",
    "textColor",
    "titleColor",
    "bodyColor",
    "descriptionColor",
    // scroll-reveal's two headline colours. Both are `color` fields, so they are
    // brand-panel-only like every other colour; without them here they fell
    // through `isLayoutField`'s catch-all and leaked a swatch into the section's
    // Layout step — the exact failure the QA Fix 3 invariant exists to catch.
    "textInside",
    "textOutside",
    "buttonStyle",
    "hideButton",
    "buttonStyleSecondary",
    "hideButtonSecondary",
    // (was `header.mobileTopBackground` — a raw colour that had to be excluded from
    // section steps; now replaced by `header.mobileSurface`, a slot `select` that
    // lives in Layout like `surface`, so no exclusion entry is needed.)
    // Shared layout controls (also responsive-capable — see RESPONSIVE_FIELD_IDS).
    "sectionSpacing",
    "sectionTextScale",
]);
/**
 * Field ids that support per-device overrides in the editor. When the seller
 * is previewing a non-desktop device, edits to these write to
 * `section.responsive[device]` instead of the base `settings`; every other
 * field always edits the base (content/structural settings can't differ by
 * viewport under SSR). Columns reuse existing per-section ids; spacing + text
 * scale are the shared fields below.
 */
export const RESPONSIVE_FIELD_IDS = new Set([
    "columnsPerRow",
    "sectionSpacing",
    "sectionTextScale",
]);
/**
 * Shared layout controls injected into every content section by the registry
 * (like `animationFields`): vertical spacing and a heading/text size scale.
 * Both are named scales (not raw px) so they map to clean CSS multipliers and
 * read well in the inspector, and both are responsive-capable so a seller can
 * tighten spacing or shrink headings on smaller screens. Defaults reproduce
 * today's look, so existing stores are unchanged.
 */
export function responsiveLayoutFields() {
    return [
        {
            id: "sectionSpacing",
            type: "select",
            label: { ar: "التباعد", en: "Spacing" },
            helpText: {
                ar: "المسافة الرأسية حوالين القسم.",
                en: "Vertical padding above and below this section.",
            },
            // Four options in one segmented row, which at 375px is four 82px cells.
            // «Comfortable» and «Spacious» filled theirs edge to edge while «Cozy»
            // rattled around in its own — a row that reads as badly fitted rather
            // than as four peers. Shorter words, same four values.
            options: [
                { value: "compact", label: { ar: "متقارب", en: "Compact" } },
                { value: "cozy", label: { ar: "مريح", en: "Cozy" } },
                { value: "comfortable", label: { ar: "واسع", en: "Comfy" } },
                { value: "spacious", label: { ar: "أوسع", en: "Airy" } },
            ],
            default: "comfortable",
        },
        {
            id: "sectionTextScale",
            type: "select",
            label: { ar: "حجم العناوين", en: "Heading size" },
            helpText: {
                ar: "يكبّر أو يصغّر عناوين القسم.",
                en: "Scales this section’s headings up or down.",
            },
            options: [
                { value: "small", label: { ar: "صغير", en: "Small" } },
                { value: "medium", label: { ar: "متوسط", en: "Medium" } },
                { value: "large", label: { ar: "كبير", en: "Large" } },
            ],
            default: "medium",
        },
    ];
}
/**
 * Universal section appearance overrides — every section uses these.
 * Optional values; empty leaves theme tokens in place.
 *
 * `surface` (2026-07) is the NEW background control and the only one the editor
 * shows. It picks a SLOT from the store's own palette rather than a raw color:
 * the seller never types a hex, and the storefront DERIVES a readable text color
 * for whatever the slot resolves to (`ensureReadable`, StorefrontThemeStyle), so
 * an unreadable background/text pairing is structurally impossible.
 *
 * It is a `select`, NOT a `color`, and deliberately absent from
 * `APPEARANCE_FIELD_IDS` — that set feeds the editor's
 * `COLOR_FIELD_IDS` (= APPEARANCE − LAYOUT), which strips color controls out of
 * section drawers (Fable Rule 1: colors are brand-panel-only). A slot choice
 * isn't a store-wide color; it affects only THIS section, so Rule 1 wants it in
 * the section drawer while the palette itself stays global. Keeping `surface`
 * out of that set is what lets it land in "How it looks".
 *
 * The five raw `color` fields below are LEGACY: no editor UI since Rule 1, but
 * still rendered by the buyer. `surface` wins over them when set.
 */
/**
 * The section-background SLOT control on its own — extracted so a section that
 * already declares its own `backgroundColor` (banner's dark promo box) can take
 * JUST this without the duplicate-id clash of spreading the whole `colorFields()`.
 */
export function surfaceField(defaultSlot = "page") {
    return {
        id: "surface",
        type: "select",
        // «القسم»/«Section» is the GROUP this sits in («إعدادات متقدمة»),
        // so repeating it in the label said the same word twice in two lines.
        label: { ar: "الخلفية", en: "Background" },
        helpText: {
            ar: "بيتاخد من ألوان متجرك — والنص بيتظبط أوتوماتيك عشان يفضل واضح.",
            en: "Uses your store's own colors — the text color is picked automatically so it stays readable.",
        },
        options: [
            { value: "page", label: { ar: "زي الصفحة", en: "Same as page" } },
            { value: "alt", label: { ar: "فاتح مختلف", en: "Subtle" } },
            { value: "primary", label: { ar: "اللون الأساسي", en: "Primary" } },
            { value: "accent", label: { ar: "اللون المميّز", en: "Accent" } },
            { value: "ink", label: { ar: "غامق", en: "Dark" } },
        ],
        // Most sections default to "Same as page"; the header defaults to Accent so
        // its long-standing bold-bar look is unchanged until a seller picks otherwise.
        default: defaultSlot,
    };
}
export function colorFields() {
    return [
        surfaceField(),
        {
            id: "backgroundColor",
            type: "color",
            label: { ar: "لون الخلفية", en: "Background color" },
            helpText: {
                ar: "سيبه فاضي عشان ياخد لون الثيم الأساسي.",
                en: "Leave empty to inherit the theme background.",
            },
        },
        {
            id: "textColor",
            type: "color",
            label: { ar: "لون النص العام", en: "General text color" },
            helpText: {
                ar: "لون افتراضي لكل النصوص. سيبه فاضي عشان ياخد لون الثيم.",
                en: "Fallback color for all text. Leave empty to inherit the theme.",
            },
        },
        {
            id: "titleColor",
            type: "color",
            label: { ar: "لون العناوين", en: "Title color" },
            helpText: {
                ar: "لون عناوين القسم. سيبه فاضي عشان ياخد لون النص العام.",
                en: "Color for section + card titles. Empty → general text color.",
            },
        },
        {
            id: "bodyColor",
            type: "color",
            label: { ar: "لون النص الأساسي", en: "Body color" },
            helpText: {
                ar: "لون فقرات النص. سيبه فاضي عشان ياخد لون النص العام.",
                en: "Color for body paragraphs. Empty → general text color.",
            },
        },
        {
            id: "descriptionColor",
            type: "color",
            label: { ar: "لون الوصف", en: "Description color" },
            helpText: {
                ar: "لون النصوص الصغيرة (مثل JOURNAL، وقت القراءة).",
                en: "Color for small labels / meta (e.g. JOURNAL, min read).",
            },
        },
    ];
}
/**
 * Button-related controls — only include for sections that actually
 * render a CTA (hero, top-seller, category-sections, banner). Skipped
 * for content-only sections like collections, all-products, stories,
 * about, faq so the inspector doesn't dangle no-op controls.
 */
export function buttonFields() {
    return [
        {
            id: "buttonStyle",
            type: "select",
            label: { ar: "نمط الزر", en: "Button style" },
            helpText: {
                ar: "بيتطبق على أزرار القسم ده.",
                en: "Applied to the CTA button(s) inside this section.",
            },
            options: [
                { value: "primary", label: { ar: "أساسي", en: "Primary" } },
                { value: "outline", label: { ar: "إطار", en: "Outline" } },
                { value: "ghost", label: { ar: "شفاف", en: "Ghost" } },
                { value: "link", label: { ar: "رابط", en: "Link" } },
            ],
            default: "primary",
        },
        {
            id: "hideButton",
            type: "checkbox",
            label: { ar: "إخفاء الزر", en: "Hide button" },
            helpText: {
                ar: "بيخفي الزر الرئيسي في القسم ده.",
                en: "Hides the CTA button(s) in this section.",
            },
            default: false,
        },
    ];
}
/**
 * Secondary-button controls — only include for sections that render a
 * second CTA next to the primary one (hero "Browse collections",
 * top-seller "View product"). Same shape as `buttonFields()` but
 * targets a separate CSS class (`.btn-section-cta-secondary`) so the
 * seller controls each button independently.
 */
export function secondaryButtonFields(showIf) {
    return [
        {
            id: "buttonStyleSecondary",
            type: "select",
            label: { ar: "نمط الزر الثاني", en: "Secondary button style" },
            helpText: {
                ar: "بيتطبق على الزر الثاني في القسم ده.",
                en: "Applied to the secondary CTA in this section.",
            },
            options: [
                { value: "primary", label: { ar: "أساسي", en: "Primary" } },
                { value: "outline", label: { ar: "إطار", en: "Outline" } },
                { value: "ghost", label: { ar: "شفاف", en: "Ghost" } },
                { value: "link", label: { ar: "رابط", en: "Link" } },
            ],
            default: "link",
            ...(showIf ? { showIf } : {}),
        },
        {
            id: "hideButtonSecondary",
            type: "checkbox",
            label: { ar: "إخفاء الزر الثاني", en: "Hide secondary button" },
            helpText: {
                ar: "بيخفي الزر الثاني بس.",
                en: "Hides only the secondary CTA.",
            },
            default: false,
            ...(showIf ? { showIf } : {}),
        },
    ];
}
/**
 * Convenience: color + button fields in one call. Use in sections that
 * have a single button; otherwise call `colorFields()` directly.
 */
export function appearanceFields() {
    return [...colorFields(), ...buttonFields()];
}
/**
 * Field ids that belong in the inspector's "Animation" group. The Inspector
 * partitions a section's settings by membership in this set, so the animation
 * controls collapse under one header on every section that declares them. The
 * registry injects `animationFields()` into every animatable section, so these
 * appear universally without each schema opting in.
 */
export const ANIMATION_FIELD_IDS = new Set([
    "animation",
    "animationSpeed",
    "animationScroll",
    "animationReplay",
    "animationLoop",
    "animationLoopSpeed",
]);
/**
 * The entrance-animation option values (everything except `none`). Used as the
 * `showIf` membership gate so the speed / scroll / replay controls only appear
 * once the seller picks a real entrance animation.
 */
const ENTRANCE_ANIMATION_VALUES = [
    "fade",
    "slide-up",
    "slide-left",
    "slide-right",
    "zoom",
    "flip",
    "blur",
    "bounce",
];
/**
 * Shared per-section animation controls — injected into every animatable
 * section by the registry, so the seller gets the same animation group
 * everywhere. Two independent layers:
 *   - **Entrance** (`animation` + speed + scroll-trigger + replay): a one-shot
 *     reveal when the section loads or scrolls into view.
 *   - **Continuous** (`animationLoop` + loop speed): an ambient never-stopping
 *     motion (float / pulse) that coexists with the entrance.
 * Default is `none` for both, so existing stores render identically until a
 * seller opts in.
 */
export function animationFields() {
    return [
        {
            id: "animation",
            type: "select",
            ui: "animation",
            label: { ar: "الحركة", en: "Animation" },
            helpText: {
                ar: "حركة ظهور القسم.",
                en: "Entrance animation for this section.",
            },
            options: [
                { value: "none", label: { ar: "بدون", en: "None" } },
                { value: "fade", label: { ar: "تلاشٍ", en: "Fade" } },
                { value: "slide-up", label: { ar: "انزلاق لأعلى", en: "Slide up" } },
                {
                    value: "slide-left",
                    label: { ar: "انزلاق لليسار", en: "Slide left" },
                },
                {
                    value: "slide-right",
                    label: { ar: "انزلاق لليمين", en: "Slide right" },
                },
                { value: "zoom", label: { ar: "تكبير", en: "Zoom" } },
                { value: "flip", label: { ar: "قلب", en: "Flip" } },
                { value: "blur", label: { ar: "ضبابية", en: "Blur" } },
                { value: "bounce", label: { ar: "ارتداد", en: "Bounce" } },
            ],
            default: "none",
        },
        {
            id: "animationSpeed",
            type: "range",
            label: { ar: "سرعة الحركة (مللي ثانية)", en: "Animation speed (ms)" },
            helpText: {
                ar: "مدة حركة الظهور.",
                en: "Duration of the entrance animation.",
            },
            min: 200,
            max: 2000,
            step: 100,
            default: 600,
            showIf: { animation: ENTRANCE_ANIMATION_VALUES },
        },
        {
            id: "animationScroll",
            type: "checkbox",
            label: { ar: "تشغيل عند الظهور", en: "Animate on scroll" },
            helpText: {
                ar: "تشغيل: الحركة تبدأ لما القسم يظهر مع التمرير. إيقاف: تبدأ مع فتح الصفحة.",
                en: "On: play when the section scrolls into view. Off: play on page load.",
            },
            default: true,
            showIf: { animation: ENTRANCE_ANIMATION_VALUES },
        },
        {
            id: "animationReplay",
            type: "checkbox",
            label: { ar: "تكرار عند إعادة الظهور", en: "Replay on re-enter" },
            helpText: {
                ar: "الحركة تتكرر كل ما القسم يرجع يظهر مع التمرير.",
                en: "Re-run the entrance each time the section re-enters the viewport.",
            },
            default: false,
            showIf: { animation: ENTRANCE_ANIMATION_VALUES, animationScroll: true },
        },
        {
            id: "animationLoop",
            type: "select",
            label: { ar: "حركة مستمرة", en: "Continuous motion" },
            helpText: {
                ar: "حركة خفيفة ما بتوقفش، منفصلة عن حركة الظهور.",
                en: "A gentle non-stop motion, independent of the entrance.",
            },
            options: [
                { value: "none", label: { ar: "بدون", en: "None" } },
                { value: "float", label: { ar: "طفو", en: "Float" } },
                { value: "pulse", label: { ar: "نبض", en: "Pulse" } },
            ],
            default: "none",
        },
        {
            id: "animationLoopSpeed",
            type: "range",
            label: {
                ar: "سرعة الحركة المستمرة (مللي ثانية)",
                en: "Continuous speed (ms)",
            },
            helpText: {
                ar: "مدة الدورة الواحدة من الحركة المستمرة.",
                en: "Duration of one continuous-motion cycle.",
            },
            min: 1500,
            max: 6000,
            step: 500,
            default: 3000,
            showIf: { animationLoop: ["float", "pulse"] },
        },
    ];
}
//# sourceMappingURL=_shared.js.map